import { ApolloClient, createQueryPreloader, from } from '@apollo/client';
import { RetryLink } from '@apollo/client/link/retry';

import { useLinks as getLinks } from '@api';
import { APPLICATION_NAME, APPLICATION_VERSION, SERVER_URI } from '@src/setup/dictionary';
import { cache } from '@store/apollo/cache';

const { splitLink, authMiddleware, errorLink, emailNormalizerLink } = getLinks();

export const client = new ApolloClient({
  cache,
  credentials: 'include',
  uri: SERVER_URI,
  name: APPLICATION_NAME,
  version: APPLICATION_VERSION,
  // Link order matters: errorLink must wrap the network terminus so it sees
  // failed responses; authMiddleware sits on top so the refresh-and-retry path
  // (errorLink → forward()) re-runs the auth context with a fresh token.
  // RetryLink handles transient network errors (5xx, dropped sockets) and is
  // intentionally placed after errorLink so we don't retry 401s 3x before
  // attempting a token refresh.
  link: from([
    // First so downstream links see normalized variables.
    emailNormalizerLink,
    authMiddleware,
    errorLink,
    new RetryLink({
      attempts: {
        max: 3,
        retryIf: (error) => Boolean(error) && error.statusCode !== 401 && error.statusCode !== 403
      },
      delay: { initial: 300, max: 3000, jitter: true }
    }),
    splitLink
  ]),
  connectToDevTools: process.env.NODE_ENV !== 'production',
  defaultOptions: {
    // Hook-driven queries (useQuery) render from cache instantly, then revalidate;
    // subsequent renders are served cache-first so scrolling past a re-mount doesn't
    // refetch. Imperative client.query() calls default to cache-first — the handful
    // of callsites that need guaranteed-fresh data opt in with fetchPolicy: 'network-only'.
    watchQuery: { fetchPolicy: 'cache-and-network', nextFetchPolicy: 'cache-first', errorPolicy: 'all' },
    query: { fetchPolicy: 'cache-first', errorPolicy: 'all' },
    mutate: { errorPolicy: 'all' }
  }
});

// https://www.apollographql.com/docs/react/api/react/preloading
export const preloadQuery = createQueryPreloader(client);

const useApolloClient = () => ({
  client,
  /**
   * Reset the Apollo store. Use during full sign-out only — `client.resetStore()`
   * also re-fires every active query, so calling this mid-session causes a
   * thundering-herd refetch.
   */
  clearCache: async (): Promise<void> => {
    await client.clearStore();
  }
});

export default useApolloClient;
